import React, { useState, useEffect } from "react";
import refreshLogo from "../img/refreshLogo.svg";
import Expand_down from "../img/Expand_down.svg";
import Add_round from "../img/Add_round.svg";
import Add_roundWhite from "../img/Add_roundWhite.svg";
import api from "../../api/axiosInstance";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import HeaderAddApp from "./HeaderAddApp";

const EditLicense = () => {
  const navigate = useNavigate();
  const [durationOptions, setDurationOptions] = useState([]);
  const [licenseData, setLicenseData] = useState({
    name: "",
    description: "",
    limits_in_count: [], // Initialize as empty array
    slab_period: "",
    slab_percentage: "",
    slab_count: "",
    slab_period_count: "",
    // ... other initial fields
  });
  const [features, setFeatures] = useState([]);
  const [durations, setDurations] = useState([]);
  const [topUps, setTopUps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [licenseLimits, setLicenseLimits] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [apiMessage, setApiMessage] = useState("");

  const [deletedDurationIds, setDeletedDurationIds] = useState([]);

  const [slabPeriodChoices, setSlabPeriodChoices] = useState([]);

  const location = useLocation();
  const { licenseId } = location.state || {};

  useEffect(() => {
    const fetchLicenseData = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const response = await api.get(
          `/admin-app/api/v1/read-license/${licenseId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = response.data.data;

        setLicenseData(data);
        setFeatures(
          data.license_features.map((f) => ({
            ...f,
            is_active: true,
          }))
        );
        setDurations(
          data.durations.map((d) => ({
            ...d,
            is_active: true,
          }))
        );
        const transformedTopUps = data.top_ups.map((item) => ({
          ...item,
          top_up_price: item.price,
          price: undefined,
        }));
        setTopUps(transformedTopUps);
        setLicenseLimits(data.project_details.license_limits || []);
      } catch (error) {
        console.error("Error fetching license data:", error);
      }
    };

    fetchLicenseData();
  }, [licenseId]);

  useEffect(() => {
    const fetchDurationOptions = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const response = await api.get("/admin-app/api/v1/list-duration", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const durations = Array.isArray(response.data?.data?.results)
          ? response.data.data.results
          : [];
        setDurationOptions(durations);
      } catch (error) {
        console.error("Error fetching duration options:", error);
        setDurationOptions([]);
      }
    };
    fetchDurationOptions();
  }, []);

  const handleSave = async () => {
    let isValid = true;
    let firstError = "";

    // Validate Plan Name
    if (!licenseData?.name?.trim()) {
      firstError = "Plan name is required.";
      isValid = false;
    }
    // Validate Plan Description
    else if (!licenseData?.description?.trim()) {
      firstError = "Plan description is required.";
      isValid = false;
    }

    // Validate License Limits - with array safety
    if (
      isValid &&
      Array.isArray(licenseData?.project_details?.license_limits)
    ) {
      const safeLimitsInCount = Array.isArray(licenseData?.limits_in_count)
        ? licenseData.limits_in_count
        : [];

      licenseData.project_details.license_limits.forEach((limitKey) => {
        const valObj = safeLimitsInCount.find(
          (item) => item && Object.prototype.hasOwnProperty.call(item, limitKey)
        );
        const value = valObj?.[limitKey];
        if (!value && value !== 0 && isValid) {
          // Allow 0 as valid value
          firstError = `Limit for ${limitKey} is required.`;
          isValid = false;
        }
      });
    }

    // Validate Features
    if (isValid) {
      for (const f of features) {
        if (!f.statement?.trim()) {
          firstError = "Feature field is required.";
          isValid = false;
          break;
        }
      }
    }

    // Validate Durations
    if (isValid) {
      for (const d of durations) {
        if (
          (!d.duration_master_id || (!d.price && d.price !== 0)) &&
          d.is_active !== false
        ) {
          firstError = "Duration and Price are required.";
          isValid = false;
          break;
        }
      }
    }

    // Validate Top-ups
    if (isValid) {
      for (const t of topUps) {
        if (
          (!t.top_up_type ||
            t.top_up_price === null ||
            t.top_up_price === undefined) &&
          t.is_active !== false
        ) {
          firstError = "Top-up type and price are required.";
          isValid = false;
          break;
        }
      }
    }

    if (!isValid) {
      setApiMessage(firstError || "Please fix all required fields.");
      setShowErrorModal(true);
      return;
    }

    const token = localStorage.getItem("accessToken");

    // Safely process limits_in_count
    const safeLimitsInCount = Array.isArray(licenseData?.limits_in_count)
      ? licenseData.limits_in_count
      : [];

    const limitsInCount = safeLimitsInCount
      .filter((limit) => limit && typeof limit === "object")
      .map((limit) => {
        const key = Object.keys(limit)[0];
        return { [key]: Number(limit[key]) || 0 };
      });

    const payload = {
      license_id: licenseData.id,
      name: licenseData.name?.trim(),
      description: licenseData.description?.trim(),
      limits_in_count: limitsInCount,
      slab_period: licenseData.slab_period || "",
      slab_percentage: licenseData.slab_percentage || "",
      slab_count: licenseData.slab_count || "",
      slab_period_count: licenseData.slab_period_count || "",
      feature_details: features.filter((f) => f.statement?.trim()), // Only include features with statements
      duration_details: durations
        .filter((d) => d.is_active !== false) // Only active durations
        .map((d) => ({
          id: d.id || null,
          duration_master_id: d.duration_master_id,
          duration_name: d.duration_name || "",
          price: Number(d.price) || 0,
          is_active: true,
        })),
      top_up_data: topUps
        .filter((t) => t.is_active !== false) // Only active top-ups
        .map((t) => ({
          id: t.id || null,
          top_up_type: t.top_up_type,
          top_up_name: t.top_up_name || t.top_up_type,
          top_up_price: Number(t.top_up_price) || 0,
          is_active: true,
        })),
    };

    try {
      setLoading(true);
      const res = await api.post("/admin-app/api/v1/edit-license", payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (res.data.status === "success") {
        setApiMessage("License updated successfully.");
        setShowSuccessModal(true);
      } else {
        setApiMessage(res.data.message || "Failed to update license.");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Failed to update license:", error);
      setApiMessage(
        error.response?.data?.message ||
          "Something went wrong. Please try again."
      );
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFeature = () =>
    setFeatures([...features, { id: null, statement: "" }]);

  const handleAddDuration = () => {
    setDurations((prev) => [
      ...prev,
      {
        id: null,
        duration_master_id: null,
        duration_name: "",
        price: "", // Keep as string for input binding
        is_active: true,
      },
    ]);
  };

  const handleAddTopUp = () => {
    setTopUps((prev) => [
      ...prev,
      {
        id: null,
        top_up_type: "",
        top_up_name: "",
        top_up_price: null,
        is_active: true, // Default for new top-ups
      },
    ]);
  };

  useEffect(() => {
    const fetchSlabPeriodChoices = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const response = await api.get("/admin-app/api/v1/edit-license", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data?.data?.slab_period_choices) {
          setSlabPeriodChoices(response.data.data.slab_period_choices);
        }
      } catch (error) {
        console.error("Error fetching slab period choices:", error);
        setSlabPeriodChoices([]); // fallback
      }
    };

    fetchSlabPeriodChoices();
  }, []);

  const handleLimitChange = (limitKey, value) => {
    const currentLimits = Array.isArray(licenseData?.limits_in_count)
      ? licenseData.limits_in_count
      : [];

    const updatedLimits = currentLimits.map((item) => {
      if (item && Object.keys(item)[0] === limitKey) {
        return { [limitKey]: value };
      }
      return item;
    });

    // If limit doesn't exist, add it
    const limitExists = updatedLimits.some(
      (item) => item && Object.keys(item)[0] === limitKey
    );

    if (!limitExists && value !== "") {
      updatedLimits.push({ [limitKey]: value });
    }

    // Filter out empty values
    const filteredLimits = updatedLimits.filter(
      (item) => item && item[Object.keys(item)[0]] !== ""
    );

    setLicenseData({
      ...licenseData,
      limits_in_count: filteredLimits,
    });
  };

  
  const getLimitValue = (limitKey) => {
    const limitsArray = Array.isArray(licenseData?.limits_in_count)
      ? licenseData.limits_in_count
      : [];

    const valueObj = limitsArray.find(
      (item) => item && Object.prototype.hasOwnProperty.call(item, limitKey)
    );
    return valueObj ? valueObj[limitKey] : "";
  };

  return (
    <div>
      <HeaderAddApp></HeaderAddApp>
      <div className="m-6">
        <div className="flex justify-between items-center font-semibold text-lg bg-[#CEC7FF4D] my-3 py-2 px-4 rounded p-3">
          <span>Edit License Details</span>
          {/* <img
            src={refreshLogo}
            alt="refresh"
            className="bg-white p-1 rounded-md"
          /> */}
        </div>

        <div className="w-full flex flex-col gap-4 text-sm">
          <div className="w-full flex gap-5">
            {/* <div className="w-1/2 relative">
              <div
                className="pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] rounded-md flex items-center justify-between bg-no-repeat bg-right cursor-pointer"
                style={{
                  backgroundImage: `url(${Expand_down})`,
                  backgroundPosition: "98% center",
                }}
              >
                <span className="text-black">
                  {licenseData?.project_details.name ||
                    "Select Application Name"}
                </span>
              </div>
            </div> */}
          </div>

          {/* Basic Plan */}
          <div
            className="w-full flex flex-col gap-5 border-1 rounded-md p-5"
            style={{ boxShadow: "0px 0px 4px 0px #00000040" }}
          >
            <span className="text-lg font-semibold">{licenseData?.name}</span>

            <div className="flex flex-col">
              <label htmlFor="" className="text-sm font-semibold ">
                Enter Plan Name
              </label>

              <input
                type="text"
                placeholder="Enter Plan Name"
                className="pl-5 h-10 mt-1 pr-4 border-2 border-[#8C8C8C4D] rounded-md"
                value={licenseData?.name || ""}
                onChange={(e) =>
                  setLicenseData({ ...licenseData, name: e.target.value })
                }
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="" className="text-sm font-semibold ">
                Enter Plan Description
              </label>

              <textarea
                placeholder="Enter Plan Description"
                className="pl-5 pt-2 mt-1 h-16 pr-4 border-2 border-[#8C8C8C4D] rounded-md"
                value={licenseData?.description || ""}
                onChange={(e) =>
                  setLicenseData({
                    ...licenseData,
                    description: e.target.value,
                  })
                }
              />
            </div>

            {/* Limit Details */}
            <div>
              <h2 className="text-sm font-semibold">Limit Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                {licenseData?.project_details?.license_limits?.map(
                  (limitKey, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-1/2 bg-[#F1EFFF] h-10 px-4 flex items-center rounded text-sm">
                        {limitKey}
                      </div>
                      <input
                        type="number"
                        className="w-1/4 h-10 px-4 border-2 border-[#8C8C8C4D] rounded-md text-sm"
                        value={getLimitValue(limitKey)}
                        onChange={(e) =>
                          handleLimitChange(limitKey, e.target.value)
                        }
                        placeholder="Enter limit"
                      />
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Features */}
            <div>
              <h2 className="text-sm font-semibold">License Features</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Enter Feature"
                      className="flex-1 pl-4 pr-4 h-10 border-2 border-[#8C8C8C4D] rounded-md"
                      value={feature.statement}
                      onChange={(e) => {
                        setFeatures((prev) =>
                          prev.map((item, i) =>
                            i === index
                              ? { ...item, statement: e.target.value }
                              : item
                          )
                        );
                      }}
                    />
                    <button
                      onClick={() =>
                        setFeatures((prev) =>
                          prev.filter((_, i) => i !== index)
                        )
                      }
                      className="w-8 h-10 flex items-center justify-center text-gray-400 font-bold text-lg"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <div
                  onClick={handleAddFeature}
                  className="flex items-center w-fit cursor-pointer py-2 px-3 bg-[#CEC7FF4D] rounded-md gap-2 hover:scale-105 transition-transform duration-300
                  text-primary hover:bg-primary hover:text-white group"
                  style={{ willChange: "transform" }}
                >
                  <img
                    src={Add_round}
                    alt=""
                    className="block group-hover:hidden"
                  />
                  <img
                    src={Add_roundWhite}
                    alt=""
                    className="hidden group-hover:block"
                  />
                  <span className=" text-sm">Add Feature</span>
                </div>
              </div>
            </div>

            {/* Durations */}
            <div>
              <h2 className="text-sm font-semibold">Set Duration</h2>
              <div className="flex flex-col gap-4 mt-1">
                {durations
                  .filter((item) => item.is_active !== false)
                  .map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      {/* Duration Dropdown */}
                      <select
                        className="flex-1 h-10 px-4 border-2 border-[#8C8C8C4D] rounded text-sm"
                        value={item.duration_master_id || ""}
                        onChange={(e) => {
                          const selectedId = parseInt(e.target.value, 10);
                          const matched = durationOptions.find(
                            (opt) => opt.id === selectedId
                          );

                          setDurations((prev) =>
                            prev.map((d, i) =>
                              i === index
                                ? {
                                    ...d,
                                    duration_master_id: matched?.id || null,
                                    duration_name: matched?.name || "",
                                  }
                                : d
                            )
                          );
                        }}
                      >
                        <option value="">Select Duration</option>
                        {durationOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>

                      {/* Price Input */}
                      <input
                        type="number"
                        placeholder="Enter Price"
                        className="w-1/4 h-10 px-4 border-2 border-[#8C8C8C4D] rounded text-sm"
                        value={item.price}
                        onChange={(e) => {
                          const value = e.target.value;
                          setDurations((prev) =>
                            prev.map((d, i) =>
                              i === index ? { ...d, price: value } : d
                            )
                          );
                        }}
                      />

                      {/* Delete Button */}
                      <button
                        onClick={() => {
                          setDurations((prev) =>
                            prev.map((d, i) =>
                              i === index ? { ...d, is_active: false } : d
                            )
                          );
                        }}
                        className="w-8 h-10 flex items-center justify-center text-gray-400 font-bold text-lg"
                      >
                        ×
                      </button>
                    </div>
                  ))}

                {/* Add Duration Button */}
                <div
                  onClick={handleAddDuration}
                  className="flex items-center w-fit cursor-pointer py-2 px-3 bg-[#CEC7FF4D] rounded-md gap-2 hover:scale-105 transition-transform duration-300 text-primary hover:text-white group hover:bg-primary"
                  style={{ willChange: "transform" }}
                >
                  <img
                    src={Add_round}
                    alt=""
                    className="block group-hover:hidden"
                  />
                  <img
                    src={Add_roundWhite}
                    alt=""
                    className="hidden group-hover:block"
                  />
                  <span className=" text-sm">Add Duration</span>
                </div>
              </div>
            </div>

            {/* Top-Ups */}
            <div>
              <h2 className="text-sm font-semibold">Set Top-Up</h2>
              <div className="flex flex-col gap-4 mt-1">
                {topUps
                  .filter((item) => item.is_active !== false) // Only show active items
                  .map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <select
                        className="flex-1 h-10 px-4 border-2 border-[#8C8C8C4D] rounded text-sm"
                        value={item.top_up_type || ""}
                        onChange={(e) => {
                          const updated = [...topUps];
                          updated[index] = {
                            ...updated[index],
                            top_up_type: e.target.value,
                            top_up_name: e.target.value,
                          };
                          setTopUps(updated);
                        }}
                      >
                        <option value="">Select a license limit</option>
                        {licenseLimits.map((limit, limitIndex) => (
                          <option key={limitIndex} value={limit}>
                            {limit}
                          </option>
                        ))}
                      </select>

                      <input
                        type="number"
                        placeholder="Enter Price"
                        className="w-1/4 h-10 px-4 border-2 border-[#8C8C8C4D] rounded text-sm"
                        value={item.top_up_price ?? ""}
                        onChange={(e) => {
                          const updated = [...topUps];
                          updated[index] = {
                            ...updated[index],
                            top_up_price:
                              e.target.value === ""
                                ? null
                                : parseFloat(e.target.value),
                          };
                          setTopUps(updated);
                        }}
                      />

                      <button
                        onClick={() => {
                          const updated = [...topUps];
                          if (updated[index].id) {
                            updated[index].is_active = false; // Mark inactive if has id
                          } else {
                            updated.splice(index, 1); // Hard delete if newly added
                          }
                          setTopUps(updated);
                        }}
                        className="w-8 h-10 flex items-center justify-center text-gray-400 font-bold text-lg"
                      >
                        ×
                      </button>
                    </div>
                  ))}

                <div
                  onClick={handleAddTopUp}
                  className="flex items-center w-fit cursor-pointer py-2 px-3 bg-[#CEC7FF4D] rounded-md gap-2 hover:bg-primary hover:text-white text-primary hover:scale-105 transition-transform duration-300 group"
                  style={{ willChange: "transform" }}
                >
                  <img
                    src={Add_round}
                    alt=""
                    className="block group-hover:hidden"
                  />
                  <img
                    src={Add_roundWhite}
                    alt=""
                    className="group-hover:block hidden"
                  />
                  <span className=" text-sm">Add Top-Up</span>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-sm font-semibold">Set Slab</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-1">
                {/* Slab Period Dropdown */}
                <select
                  className="h-10 px-4 border-2 border-[#8C8C8C4D] rounded text-sm"
                  value={licenseData?.slab_period || ""}
                  onChange={(e) =>
                    setLicenseData({
                      ...licenseData,
                      slab_period: e.target.value,
                    })
                  }
                >
                  <option value="">Select Slab Period</option>
                  {slabPeriodChoices.map((choice, index) => (
                    <option key={index} value={choice}>
                      {choice}
                    </option>
                  ))}
                </select>

                {/* Slab % - Integer */}
                <input
                  type="number"
                  placeholder="Slab %"
                  className="h-10 px-4 border-2 border-[#8C8C8C4D] rounded text-sm"
                  value={licenseData?.slab_percentage || ""}
                  onChange={(e) =>
                    setLicenseData({
                      ...licenseData,
                      slab_percentage:
                        e.target.value === ""
                          ? ""
                          : parseInt(e.target.value, 10),
                    })
                  }
                />

                {/* Slab Count - Integer */}
                <input
                  type="number"
                  placeholder="Slab Count"
                  className="h-10 px-4 border-2 border-[#8C8C8C4D] rounded text-sm"
                  value={licenseData?.slab_count || ""}
                  onChange={(e) =>
                    setLicenseData({
                      ...licenseData,
                      slab_count:
                        e.target.value === ""
                          ? ""
                          : parseInt(e.target.value, 10),
                    })
                  }
                />

                {/* Slab Period Count - Integer */}
                <input
                  type="number"
                  placeholder="Slab Period Count"
                  className="h-10 px-4 border-2 border-[#8C8C8C4D] rounded text-sm"
                  value={licenseData?.slab_period_count || ""}
                  onChange={(e) =>
                    setLicenseData({
                      ...licenseData,
                      slab_period_count:
                        e.target.value === ""
                          ? ""
                          : parseInt(e.target.value, 10),
                    })
                  }
                />
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="w-full flex justify-end gap-4 mt-8">
            <button
              onClick={handleSave}
              className="bg-primary w-44 py-1 text-white font-semibold rounded-md text-lg hover:scale-105 transition-transform duration-300"
              disabled={loading}
              style={{ willChange: "transform" }}
            >
              {loading ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => {
                navigate(-1);
              }}
              className="border border-primary text-primary w-44 py-1 font-semibold rounded-md text-lg bg-transparent hover:scale-105 transition-transform duration-300"
              style={{ willChange: "transform" }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Error Modal */}
      <AnimatePresence>
        {showErrorModal && (
          <>
            {/* Background Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black bg-opacity-50"
            />

            {/* Modal Wrapper */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 20, opacity: 0 }}
                className="w-full max-w-md bg-white rounded-xl shadow-xl"
              >
                {/* Red Circle X Icon */}
                <div className="flex justify-center pt-8">
                  <motion.div
                    className="flex items-center justify-center w-20 h-20 rounded-full bg-red-50"
                    animate={{
                      rotate: [0, -10, 10, -10, 10, 0], // shake motion
                    }}
                    transition={{
                      duration: 0.6,
                      ease: "easeInOut",
                    }}
                  >
                    <svg
                      className="w-8 h-8 text-red-500"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        strokeWidth={2}
                        strokeLinecap="round"
                        d="M12 8v4m0 4h.01M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"
                      />
                    </svg>
                  </motion.div>
                </div>

                {/* Message */}
                <div className="px-6 py-4 text-center">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    Oops!
                  </h3>
                  <p className="text-gray-600 mb-6">{apiMessage}</p>
                </div>

                {/* Button */}
                <div className="px-6 pb-6">
                  <button
                    onClick={() => setShowErrorModal(false)}
                    className="w-full py-3 px-4 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg"
                  >
                    Try Again
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccessModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black bg-opacity-50"
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 20, opacity: 0 }}
                className="w-full max-w-md bg-white rounded-xl shadow-xl"
              >
                <div className="flex justify-center pt-8">
                  <motion.div
                    className="flex items-center justify-center w-20 h-20 rounded-full bg-green-50"
                    animate={{
                      rotate: [0, -10, 10, -10, 10, 0], // shake animation
                    }}
                    transition={{
                      duration: 0.6,
                      ease: "easeInOut",
                    }}
                  >
                    <svg
                      className="w-12 h-12 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </motion.div>
                </div>
                <div className="px-6 py-4 text-center">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    Success!
                  </h3>
                  <p className="text-gray-600 mb-6">{apiMessage}</p>
                </div>
                <div className="px-6 pb-6">
                  <button
                    onClick={() => {
                      setShowSuccessModal(false);
                      navigate(-1);
                    }}
                    className="w-full py-3 px-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-lg"
                  >
                    Continue
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EditLicense;
